/**
 * 4-А1 «Создать салон» — правила формы.
 *
 * ГЛАВНОЕ ПРАВИЛО ЭКРАНА И ЕДИНСТВЕННАЯ ПРИЧИНА, ПО КОТОРОЙ ЭТОТ ФАЙЛ СУЩЕСТВУЕТ
 * ОТДЕЛЬНО ОТ ВЁРСТКИ: оферта и политика обработки данных — ДВА РАЗНЫХ ДОКУМЕНТА,
 * и согласие на них даётся двумя разными отметками. Один флажок «принимаю условия»
 * на оба документа юридически не является согласием на обработку персональных данных:
 * согласие обязано быть конкретным и отделимым. Это ровно тот случай, когда «упростить
 * форму» означает «сделать её недействительной», поэтому правило вынесено под проверку,
 * а не оставлено в разметке, где его однажды схлопнут в один чекбокс ради красоты.
 *
 * Адрес страницы записи — второе место, где ошибка необратима: на него встают ссылки
 * в боте и соцсетях, и сменить его потом нельзя без последствий. Отсюда и проверка
 * вида, и предупреждение рядом с полем.
 */

/** Форма собственности салона. Значения — из макета, порядок тоже. */
export const LEGAL_FORMS = ["ИП", "ООО", "Самозанятый", "Физлицо"] as const
export type LegalForm = (typeof LEGAL_FORMS)[number]

export interface CreateSalonDraft {
  readonly salonName: string
  readonly slug: string
  readonly contact: string
  readonly ownerName: string
  readonly timezone: string
  readonly legalForm: LegalForm
  /** Оферта. Отдельный документ. */
  readonly offerAccepted: boolean
  /** Политика обработки данных. ДРУГОЙ отдельный документ. */
  readonly privacyAccepted: boolean
}

export type SlugCheck =
  /** Ещё не проверяли или поле пустое. */
  | { readonly kind: "idle" }
  | { readonly kind: "checking" }
  | { readonly kind: "free" }
  | { readonly kind: "taken" }
  /** Вид адреса не годится — это видно без сервера. */
  | { readonly kind: "invalid"; readonly reason: string }

export interface CreateSalonValidation {
  readonly canSubmit: boolean
  /** Ошибка поля → текст. Пустой объект — всё в порядке. */
  readonly salonNameError: string | null
  readonly slugError: string | null
  readonly contactError: string | null
  /** Красная строка под парой согласий. Та самая, из макета. */
  readonly consentError: string | null
}

export const TEXT_TWO_DOCUMENTS = "Это два отдельных документа — откройте и отметьте оба."

/** Предупреждение про необратимость адреса. Стоит рядом с полем всегда, а не при ошибке. */
export const TEXT_SLUG_FOREVER =
  "Поменять адрес потом будет нельзя без последствий: на него встанут ссылки в боте и соцсетях."

/**
 * Название салона → адрес страницы записи.
 *
 * Транслитерация нужна, потому что название пишут по-русски, а адрес обязан быть
 * латиницей: «Барбершоп на Ленина» → «barbershop-na-lenina». Без неё владелец
 * придумывает адрес сам, в спешке, и получает «bar1» навсегда.
 */
const TRANSLIT: Readonly<Record<string, string>> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh", з: "z", и: "i",
  й: "i", к: "k", л: "l", м: "m", н: "n", о: "o", п: "p", р: "r", с: "s", т: "t",
  у: "u", ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "",
  э: "e", ю: "yu", я: "ya",
}

export function slugFromName(name: string): string {
  const latin = Array.from(name.toLowerCase())
    .map((char) => TRANSLIT[char] ?? char)
    .join("")
  return latin
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-+|-+$/g, "")
    .slice(0, 40)
}

/**
 * Годится ли адрес по виду. Сервер всё равно проверит занятость, но форма обязана
 * поймать «Барбершоп на Ленина» в поле адреса раньше, чем человек нажмёт кнопку.
 */
export function checkSlugShape(slug: string): SlugCheck {
  if (slug.trim().length === 0) return { kind: "idle" }
  if (slug.length < 3) {
    return { kind: "invalid", reason: "Адрес короче трёх знаков — его будет трудно диктовать." }
  }
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(slug)) {
    return {
      kind: "invalid",
      reason: "Только латиница, цифры и дефис внутри — так адрес переживёт пересылку в мессенджере.",
    }
  }
  return { kind: "idle" }
}

export function validateCreateSalon(
  draft: CreateSalonDraft,
  slugCheck: SlugCheck,
): CreateSalonValidation {
  const salonNameError =
    draft.salonName.trim().length === 0 ? "Без названия салон не найдут ни клиенты, ни вы." : null

  const shape = checkSlugShape(draft.slug)
  const slugError =
    shape.kind === "invalid"
      ? shape.reason
      : slugCheck.kind === "taken"
        ? "Этот адрес уже занят — придумайте другой."
        : draft.slug.trim().length === 0
          ? "Без адреса страницу записи некуда поставить."
          : null

  const contactError =
    draft.contact.trim().length === 0
      ? "Телефон или почта нужны, чтобы вернуть вам доступ, если пароль потеряется."
      : null

  // Ровно то правило, ради которого файл и написан: НЕ «хотя бы одна отметка»
  // и НЕ «отметка одна на оба документа».
  const bothConsents = draft.offerAccepted && draft.privacyAccepted
  const consentError = bothConsents ? null : TEXT_TWO_DOCUMENTS

  return {
    canSubmit:
      salonNameError === null &&
      slugError === null &&
      contactError === null &&
      bothConsents &&
      slugCheck.kind !== "checking",
    salonNameError,
    slugError,
    contactError,
    consentError,
  }
}
